/**
 * WontRip Multi-Reservation System v1.0
 * Client-side reservation "cart" using localStorage
 *
 * Per SK Zákon 190/2003: firearms (Kat A/B/C) + ammo cannot be sold online.
 * This module lets users build a list of items to reserve, then submit via contact form.
 *
 * API:
 *   WontRipReservation.add({handle, title, price, image, variantId, url})
 *   WontRipReservation.remove(handle)
 *   WontRipReservation.clear()
 *   WontRipReservation.getItems() → Array<Item>
 *   WontRipReservation.count() → number
 *   WontRipReservation.onChange(cb) → unsubscribe fn
 *
 * Events emitted on window:
 *   'wontrip:reservation:changed' → detail: { items, count }
 *   'wontrip:reservation:added'   → detail: { item }
 *   'wontrip:reservation:removed' → detail: { handle }
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'wontrip_reservation_v1';
  const listeners = new Set();

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (e) {
      console.warn('[WontRipReservation] Failed to read:', e);
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      emit('wontrip:reservation:changed', { items, count: items.length });
      listeners.forEach(cb => { try { cb(items); } catch(e){} });
    } catch (e) {
      console.warn('[WontRipReservation] Failed to write:', e);
    }
  }

  function emit(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch (e) {}
  }

  const WontRipReservation = {
    add(item) {
      if (!item || !item.handle) return false;
      const items = read();
      const existing = items.findIndex(i => i.handle === item.handle);
      const normalized = {
        handle: String(item.handle),
        title: String(item.title || item.handle),
        price: item.price || '',
        image: item.image || '',
        variantId: item.variantId || '',
        url: item.url || ('/products/' + item.handle),
        addedAt: new Date().toISOString()
      };
      if (existing >= 0) {
        // already in list — don't duplicate, just refresh timestamp
        items[existing] = { ...items[existing], ...normalized };
      } else {
        items.push(normalized);
      }
      write(items);
      emit('wontrip:reservation:added', { item: normalized });
      this.showToast('Pridané do rezervácie: ' + normalized.title);
      return true;
    },

    remove(handle) {
      const items = read().filter(i => i.handle !== handle);
      write(items);
      emit('wontrip:reservation:removed', { handle });
      return true;
    },

    clear() {
      write([]);
      return true;
    },

    getItems() {
      return read();
    },

    count() {
      return read().length;
    },

    has(handle) {
      return read().some(i => i.handle === handle);
    },

    onChange(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },

    showToast(message) {
      let toast = document.getElementById('wontrip-res-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'wontrip-res-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%) translateY(100px);background:#d97706;color:#fff;padding:12px 20px;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,.2);z-index:10000;font-weight:600;font-size:.95rem;transition:transform .3s ease;max-width:90vw;text-align:center;pointer-events:none;opacity:0;';
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      requestAnimationFrame(() => {
        toast.style.transform = 'translateX(-50%) translateY(0)';
        toast.style.opacity = '1';
      });
      clearTimeout(toast._hideTimer);
      toast._hideTimer = setTimeout(() => {
        toast.style.transform = 'translateX(-50%) translateY(100px)';
        toast.style.opacity = '0';
      }, 2500);
    }
  };

  // Auto-update header badge if present
  function updateBadge() {
    const count = WontRipReservation.count();
    document.querySelectorAll('[data-wontrip-res-count]').forEach(el => {
      el.textContent = count;
      el.setAttribute('data-count', count);
      if (count > 0) el.classList.add('has-items');
      else el.classList.remove('has-items');
    });
    document.querySelectorAll('[data-wontrip-res-badge]').forEach(el => {
      if (count > 0) {
        el.classList.add('visible');
        el.style.display = '';
      } else {
        el.classList.remove('visible');
      }
    });
  }

  window.addEventListener('wontrip:reservation:changed', updateBadge);
  if (document.readyState !== 'loading') updateBadge();
  else document.addEventListener('DOMContentLoaded', updateBadge);

  // Sync across browser tabs
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      emit('wontrip:reservation:changed', { items: read(), count: read().length });
    }
  });

  window.WontRipReservation = WontRipReservation;

  // Intercept quick-add form submits for firearms (backend protection too)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wontrip-reserve]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const item = {
      handle: btn.dataset.productHandle,
      title: btn.dataset.productTitle,
      price: btn.dataset.productPrice,
      image: btn.dataset.productImage,
      variantId: btn.dataset.variantId,
      url: btn.dataset.productUrl || ('/products/' + btn.dataset.productHandle)
    };
    WontRipReservation.add(item);
  }, true);
})();
